import { useMemo, useState } from 'react';
import { Eye, EyeOff, KeyRound, PauseCircle, PlayCircle, Search, Trash2, Users } from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '@/lib/api';
import { useSuperMutations, useSuperOverview, useSuperUser, useSuperUsers } from '@/lib/queries';
import { CredentialsDialog } from '@/components/CredentialsDialog';
import type { ProvisionedCredentials } from '@nlinks/shared';
import { formatNumber, formatRelative } from '@/lib/format';
import { Pagination } from '@/components/Pagination';
import { StatCard } from '@/components/StatCard';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  Modal,
  Spinner,
  TableSkeleton,
  humanizeStatus,
  statusTone,
  useToast,
} from '@/components/ui';

/**
 * Detail drawer for one user, including their mailbox credentials.
 *
 * Passwords are fetched only when explicitly revealed — a separate request
 * that the server audits — rather than being included in the page payload and
 * hidden with CSS.
 */
function UserDetail({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [reveal, setReveal] = useState(false);
  const { data, isLoading } = useSuperUser(userId, reveal);

  return (
    <Modal open onClose={onClose} title={data?.fullName ?? 'User'} description={data?.email} size="xl">
      {isLoading || !data ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <div className="mt-1">
                <Badge tone={statusTone(data.status)}>{humanizeStatus(data.status)}</Badge>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500">Tier</p>
              <div className="mt-1">
                <Badge tone="brand">{data.tier}</Badge>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500">Created by</p>
              <p className="mt-1 text-sm text-slate-300">{data.createdByName ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Last sign-in</p>
              <p className="mt-1 text-sm text-slate-300">{formatRelative(data.lastLoginAt)}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: 'Mails matched', value: data.totals.messagesMatched, tone: 'text-slate-100' },
              { label: 'Links found', value: data.totals.linksFound, tone: 'text-slate-100' },
              { label: 'Mails opened', value: data.totals.mailsOpened, tone: 'text-emerald-300' },
              { label: 'Not opened', value: data.totals.mailsNotOpened, tone: 'text-red-300' },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-surface-border bg-surface-base p-3">
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className={clsx('mt-1 text-lg font-semibold tabular-nums', s.tone)}>{formatNumber(s.value)}</p>
              </div>
            ))}
          </div>

          {data.keywordBatches.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Active keyword batches ({data.keywordBatches.length})
              </p>
              {data.keywordBatches.map((batch) => (
                <div key={batch.label} className="rounded-lg border border-surface-border bg-surface-base p-3">
                  <p className="mb-2 text-xs font-medium text-slate-300">{batch.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {batch.keywords.slice(0, 20).map((keyword) => (
                      <Badge key={keyword} tone="brand">
                        {keyword}
                      </Badge>
                    ))}
                    {batch.keywords.length > 20 ? (
                      <span className="text-xs text-slate-500">+{batch.keywords.length - 20} more</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert severity="warning">No active keywords — this user&rsquo;s mailboxes are not being checked.</Alert>
          )}

          {/*
            The account's own sign-in password, kept beside the mailbox app
            passwords because both answer the same question. Administrator
            passwords are one-way hashed and cannot be produced, so that case is
            stated rather than shown as an empty field.
          */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Login password</p>
            <div className="rounded-lg border border-surface-border bg-surface-base px-3 py-2.5">
              {!data.loginPasswordAvailable ? (
                <p className="text-xs text-slate-500">
                  Not on record. Administrator passwords are stored one-way, and any account that has not
                  set a password since this was enabled has nothing to show. Use{' '}
                  <span className="text-slate-400">Reset password</span> to issue one.
                </p>
              ) : !reveal ? (
                <p className="text-xs text-slate-500">Hidden — use “Reveal passwords” below.</p>
              ) : (
                <code className="break-all font-mono text-sm text-amber-300">{data.loginPassword}</code>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Mailboxes ({data.mailboxes.length})
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setReveal((v) => !v)}
                icon={reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              >
                {reveal ? 'Hide passwords' : 'Reveal passwords'}
              </Button>
            </div>

            {reveal ? (
              <Alert severity="warning" className="mb-3">
                The sign-in password above and the app passwords below are decrypted. This reveal is
                recorded in the audit log.
              </Alert>
            ) : null}

            {data.mailboxes.length === 0 ? (
              <p className="text-sm text-slate-600">No mailboxes connected.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-border">
                <table className="table-base">
                  <thead>
                    <tr>
                      <th>Email address</th>
                      <th>App password</th>
                      <th>Status</th>
                      <th className="text-right">Last check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mailboxes.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <p className="font-mono text-xs text-slate-200">{m.emailAddress}</p>
                          <p className="text-xs text-slate-600">{m.label}</p>
                        </td>
                        <td>
                          {m.appPassword ? (
                            <code className="break-all font-mono text-xs text-amber-300">{m.appPassword}</code>
                          ) : (
                            <span className="font-mono text-xs text-slate-700">••••••••••••</span>
                          )}
                        </td>
                        <td>
                          <Badge tone={m.syncEnabled ? statusTone(m.syncStatus) : 'neutral'}>
                            {m.syncEnabled ? humanizeStatus(m.syncStatus) : 'Paused'}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap text-right text-xs text-slate-500">
                          {formatRelative(m.lastSyncAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SuperUsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState<{ id: string; name: string } | null>(null);
  const [newCredentials, setNewCredentials] = useState<{ credentials: ProvisionedCredentials; name: string } | null>(null);

  const { push } = useToast();
  const { deleteUser, resetPassword, toggleHold } = useSuperMutations();
  const overview = useSuperOverview();

  const params = useMemo(() => ({ page, pageSize: 25, search: search || undefined }), [page, search]);
  const { data, isLoading, isPlaceholderData } = useSuperUsers(params);

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await deleteUser.mutateAsync(confirmDelete.id);
      push('success', `${confirmDelete.name} removed.`);
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not remove that user.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const doReset = async () => {
    if (!confirmReset) return;
    try {
      const credentials = await resetPassword.mutateAsync(confirmReset.id);
      setNewCredentials({ credentials, name: confirmReset.name });
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not reset that password.');
    } finally {
      setConfirmReset(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Users</h1>
        <p className="mt-1 text-sm text-slate-500">Every account on the platform.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total users"
          value={formatNumber(overview.data?.users.total ?? 0)}
          icon={<Users className="h-4 w-4" />}
          tone="brand"
          info="End users only. Administrators are counted separately, on the Administrators screen."
          loading={overview.isLoading}
        />
        <StatCard
          label="Active"
          value={formatNumber(overview.data?.users.active ?? 0)}
          tone="success"
          info="Users who can sign in and whose mailboxes are being checked."
          loading={overview.isLoading}
        />
        <StatCard
          label="On hold"
          value={formatNumber(overview.data?.users.onHold ?? 0)}
          tone={overview.data && overview.data.users.onHold > 0 ? 'warning' : 'default'}
          info="Blocked from signing in, and none of their mailboxes are checked. An administrator can release them at any time."
          loading={overview.isLoading}
        />
        <StatCard
          label="Free tier"
          value={formatNumber(overview.data?.users.byTier.FREE ?? 0)}
          sublabel="all users are on FREE"
          info="Tiers are recorded but not yet enforced — no limits are applied to any account."
          loading={overview.isLoading}
        />
      </div>

      <Card>
        <CardHeader
          title="All users"
          description="Click a row to see their mailboxes, keywords, and app passwords"
        />
        <div className="border-b border-surface-border p-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" aria-hidden />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search name or email…"
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={8} columns={6} />
        ) : data?.items.length ? (
          <>
            <div className={clsx('overflow-x-auto transition-opacity', isPlaceholderData && 'opacity-60')}>
              <table className="table-base">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Tier</th>
                    <th className="text-center">Mailboxes</th>
                    <th>Created by</th>
                    <th className="text-right">Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id} className="cursor-pointer" onClick={() => setOpenUser(user.id)}>
                      <td>
                        <p className="font-medium text-slate-200">{user.fullName}</p>
                        <p className="truncate text-xs text-slate-500">{user.email}</p>
                      </td>
                      <td>
                        <Badge tone={statusTone(user.status)}>{humanizeStatus(user.status)}</Badge>
                      </td>
                      <td>
                        <Badge tone="brand">{user.tier}</Badge>
                      </td>
                      <td className="text-center tabular-nums text-slate-300">{user.mailboxCount}</td>
                      <td className="text-xs text-slate-500">{user.createdByName ?? '—'}</td>
                      <td className="whitespace-nowrap text-right text-xs text-slate-500">
                        {formatRelative(user.lastLoginAt)}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmReset({ id: user.id, name: user.fullName });
                            }}
                            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-surface-hover hover:text-brand-300"
                            aria-label={`Reset password for ${user.fullName}`}
                            title="Reset password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleHold.mutateAsync(user.id);
                            }}
                            className={clsx(
                              'rounded-lg p-1.5 transition',
                              user.status === 'ON_HOLD'
                                ? 'text-amber-400 hover:bg-amber-500/10'
                                : 'text-slate-500 hover:bg-surface-hover hover:text-amber-300',
                            )}
                            aria-label={
                              user.status === 'ON_HOLD'
                                ? `Release ${user.fullName}`
                                : `Freeze ${user.fullName}`
                            }
                            title={
                              user.status === 'ON_HOLD'
                                ? 'Release — lets them sign in again and resumes their mailboxes'
                                : 'Freeze — blocks sign-in and pauses every mailbox'
                            }
                          >
                            {user.status === 'ON_HOLD' ? (
                              <PlayCircle className="h-4 w-4" />
                            ) : (
                              <PauseCircle className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete({ id: user.id, name: user.fullName });
                            }}
                            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-500/10 hover:text-red-400"
                            aria-label={`Remove ${user.fullName}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...data} onPageChange={setPage} />
          </>
        ) : (
          <EmptyState icon={<Users className="h-6 w-6" />} title="No users yet" />
        )}
      </Card>

      {openUser ? <UserDetail userId={openUser} onClose={() => setOpenUser(null)} /> : null}

      {newCredentials ? (
        <CredentialsDialog
          email={newCredentials.credentials.email}
          password={newCredentials.credentials.temporaryPassword}
          fullName={newCredentials.name}
          onClose={() => setNewCredentials(null)}
        />
      ) : null}

      <Modal
        open={Boolean(confirmReset)}
        onClose={() => setConfirmReset(null)}
        title="Reset this user's password?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmReset(null)}>
              Cancel
            </Button>
            <Button onClick={doReset} loading={resetPassword.isPending}>
              Reset password
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          A new password is generated for{' '}
          <span className="font-medium text-slate-200">{confirmReset?.name}</span> and shown once. Their sessions end
          immediately and they must choose their own password at next sign-in.
        </p>
        <p className="mt-3 text-xs text-slate-600">
          Existing passwords cannot be displayed — they are stored only as a one-way hash, so no copy exists to read.
        </p>
      </Modal>

      <Modal
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Remove this user?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={remove} loading={deleteUser.isPending}>
              Remove user
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          <span className="font-medium text-slate-200">{confirmDelete?.name}</span> will lose access immediately and
          all their mailboxes stop syncing.
        </p>
      </Modal>
    </div>
  );
}
